import { useNavigate } from "react-router-dom";
import {
	ShieldCheck,
	Database,
	Target,
	Scale,
	Share2,
	Lock,
	Cookie,
	UserCheck,
	Trash2,
	History,
	Mail,
	ArrowLeft,
} from "lucide-react";

const LAST_UPDATED = "05 de agosto de 2026";

const sections = [
	{
		icon: Database,
		title: "1. Quais dados coletamos",
		content: (
			<>
				<p>
					Coletamos dados fornecidos diretamente por você (ou pela sua empresa) ao contratar e utilizar o SZ
					Sistemas, como:
				</p>
				<ul className="list-disc pl-6 space-y-1 mt-3">
					<li>Dados cadastrais: nome, e-mail, telefone, CPF/CNPJ e endereço da empresa e de colaboradores;</li>
					<li>
						Dados operacionais do seu negócio: cadastros de produtos, clientes, fornecedores, vendas,
						estoque e movimentações financeiras registradas no sistema;
					</li>
					<li>Dados de acesso: login, permissões de uso e registros de atividade (logs) na plataforma;</li>
					<li>
						Dados técnicos coletados automaticamente: endereço IP, tipo de dispositivo, navegador e
						cookies.
					</li>
				</ul>
			</>
		),
	},
	{
		icon: Target,
		title: "2. Para que usamos os seus dados",
		content: (
			<ul className="list-disc pl-6 space-y-1">
				<li>Viabilizar o funcionamento do sistema (PDV, estoque, financeiro, relatórios e emissão fiscal);</li>
				<li>Autenticar acessos e controlar permissões de usuários e colaboradores;</li>
				<li>Prestar suporte técnico e atendimento;</li>
				<li>Enviar comunicações sobre o serviço, atualizações e cobranças;</li>
				<li>Cumprir obrigações legais, fiscais e regulatórias;</li>
				<li>Melhorar a plataforma a partir de métricas de uso.</li>
			</ul>
		),
	},
	{
		icon: Scale,
		title: "3. Base legal (LGPD)",
		content: (
			<p>
				O tratamento de dados pessoais pelo SZ Sistemas segue a Lei Geral de Proteção de Dados (Lei nº
				13.709/2018 - LGPD), com base, conforme o caso, na execução de contrato, no cumprimento de obrigação
				legal ou regulatória, no legítimo interesse do controlador e, quando aplicável, no seu consentimento.
			</p>
		),
	},
	{
		icon: Share2,
		title: "4. Compartilhamento de dados",
		content: (
			<>
				<p>Não vendemos seus dados. Eles podem ser compartilhados apenas quando necessário, com:</p>
				<ul className="list-disc pl-6 space-y-1 mt-3">
					<li>
						Prestadores de serviço que apoiam a operação da plataforma (hospedagem em nuvem, emissão
						fiscal, processamento de pagamentos, envio de mensagens/WhatsApp);
					</li>
					<li>Autoridades públicas, quando exigido por lei, ordem judicial ou regulação fiscal;</li>
					<li>Parceiros de suporte técnico, mediante acordo de confidencialidade.</li>
				</ul>
			</>
		),
	},
	{
		icon: Lock,
		title: "5. Armazenamento e segurança",
		content: (
			<p>
				Seus dados são armazenados em servidores em nuvem, com controles de acesso, criptografia em trânsito e
				backups periódicos. Adotamos medidas técnicas e administrativas razoáveis para proteger os dados
				contra acessos não autorizados, perda, alteração ou vazamento.
			</p>
		),
	},
	{
		icon: Cookie,
		title: "6. Cookies",
		content: (
			<p>
				Utilizamos cookies e tecnologias semelhantes para manter sua sessão ativa, lembrar preferências e
				entender como o sistema é utilizado. Você pode gerenciar ou desativar cookies nas configurações do seu
				navegador, embora isso possa afetar o funcionamento de algumas partes do sistema.
			</p>
		),
	},
	{
		icon: UserCheck,
		title: "7. Seus direitos como titular de dados",
		content: (
			<>
				<p>Nos termos da LGPD, você pode solicitar, a qualquer momento:</p>
				<ul className="list-disc pl-6 space-y-1 mt-3">
					<li>Confirmação da existência de tratamento e acesso aos seus dados;</li>
					<li>Correção de dados incompletos, inexatos ou desatualizados;</li>
					<li>Anonimização, bloqueio ou eliminação de dados desnecessários ou tratados em desconformidade;</li>
					<li>Portabilidade dos dados a outro fornecedor de serviço;</li>
					<li>Revogação do consentimento e informações sobre entidades com as quais compartilhamos dados.</li>
				</ul>
			</>
		),
	},
	{
		icon: Trash2,
		title: "8. Retenção e eliminação",
		content: (
			<p>
				Mantemos seus dados pelo tempo necessário para cumprir as finalidades descritas nesta política e as
				obrigações legais e fiscais aplicáveis (ex.: guarda de documentos fiscais). Após esse período, os
				dados são eliminados ou anonimizados de forma segura, exceto quando a lei exigir sua manutenção.
			</p>
		),
	},
	{
		icon: History,
		title: "9. Alterações nesta política",
		content: (
			<p>
				Esta Política de Privacidade pode ser atualizada periodicamente para refletir melhorias no sistema ou
				mudanças legais. A data da última atualização é sempre indicada no topo desta página. Recomendamos
				revisá-la com regularidade.
			</p>
		),
	},
];

function PoliticaDePrivacidade() {
	const navigate = useNavigate();

	return (
		<div className="min-h-screen flex flex-col font-sans bg-gray-50 antialiased">
			{/* Navbar */}
			<header className="w-full bg-white shadow-sm sticky top-0 z-50">
				<div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
					<div
						className="flex-shrink-0 flex items-center gap-2 cursor-pointer group"
						onClick={() => navigate("/home")}
					>
						<div className="bg-gradient-to-br from-blue-600 to-blue-900 text-white w-9 h-9 rounded-xl shadow-[0_4px_10px_-2px_rgba(37,99,235,0.4)] flex items-center justify-center transform transition-all group-hover:scale-105 group-hover:-translate-y-0.5 relative z-10 overflow-hidden">
							<span className="font-extrabold text-xl leading-none tracking-tight translate-z-0 drop-shadow-sm px-1 mt-0.5">
								SZ
							</span>
						</div>
						<div className="hidden sm:flex flex-col justify-center antialiased">
							<span className="font-black text-xl leading-none text-gray-900 tracking-tight">
								Sistemas
							</span>
							<span className="text-[10px] font-bold text-blue-600 tracking-widest uppercase leading-none mt-1">
								Gestão Inteligente
							</span>
						</div>
					</div>

					<button
						onClick={() => navigate("/home")}
						className="flex items-center gap-2 text-gray-600 hover:text-blue-600 font-medium transition-colors text-sm"
					>
						<ArrowLeft size={16} />
						Voltar ao site
					</button>
				</div>
			</header>

			{/* Hero */}
			<section className="bg-gradient-to-b from-blue-900 to-blue-800 text-white py-16">
				<div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
					<div className="inline-flex items-center justify-center w-16 h-16 bg-blue-800/50 border border-blue-400/30 rounded-full mb-6">
						<ShieldCheck size={28} />
					</div>
					<h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">Política de Privacidade</h1>
					<p className="text-blue-100">Última atualização: {LAST_UPDATED}</p>
				</div>
			</section>

			{/* Conteúdo */}
			<main className="flex-1 py-16">
				<div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-8 sm:p-10 mb-8">
						<p className="text-gray-600 leading-relaxed">
							A <strong className="text-gray-900">SZ Sistemas</strong> respeita a sua privacidade e está
							comprometida com a proteção dos dados pessoais tratados por meio da nossa plataforma de
							gestão (SZ PDV). Esta política explica quais dados coletamos, como e por que os usamos, com
							quem podemos compartilhá-los e quais são os seus direitos, em conformidade com a Lei Geral
							de Proteção de Dados (Lei nº 13.709/2018).
						</p>
					</div>

					<div className="space-y-6">
						{sections.map(({ icon: Icon, title, content }) => (
							<div
								key={title}
								className="bg-white border border-gray-100 rounded-2xl shadow-sm p-8 sm:p-10 hover:border-blue-200 transition-all"
							>
								<div className="flex items-center gap-3 mb-4">
									<div className="w-10 h-10 shrink-0 bg-blue-100 text-blue-700 rounded-lg flex items-center justify-center">
										<Icon size={20} />
									</div>
									<h2 className="text-xl font-bold text-gray-900">{title}</h2>
								</div>
								<div className="text-gray-600 leading-relaxed">{content}</div>
							</div>
						))}

						{/* Contato */}
						<div className="bg-blue-900 text-white rounded-2xl shadow-sm p-8 sm:p-10">
							<div className="flex items-center gap-3 mb-4">
								<div className="w-10 h-10 shrink-0 bg-blue-800/50 border border-blue-400/30 rounded-lg flex items-center justify-center">
									<Mail size={20} />
								</div>
								<h2 className="text-xl font-bold">10. Fale com a gente</h2>
							</div>
							<p className="text-blue-100 leading-relaxed">
								Para dúvidas sobre esta Política de Privacidade ou para exercer seus direitos como
								titular de dados, entre em contato com a nossa equipe pelo WhatsApp através do site ou
								pelos canais de suporte disponíveis dentro do sistema.
							</p>
						</div>
					</div>
				</div>
			</main>

			{/* Rodapé */}
			<footer className="bg-gray-900 text-gray-400 py-8 border-t-4 border-blue-600">
				<div className="max-w-5xl mx-auto px-6 text-center text-sm">
					<p>© {new Date().getFullYear()} - SZ Sistemas. Todos os direitos reservados.</p>
				</div>
			</footer>
		</div>
	);
}

export default PoliticaDePrivacidade;
